import { itemsCol } from '../../shared/array-types';
import { fromBaseRecord } from '../compile/from-base-context';
import { is_numeric } from '../php-compat/values';
import { isUuid } from '../reactions/layer-list-ids';
import type { AuthoringFinding } from './publish-validation';

const fields = ['inventory_equipped', 'inventory_unequipped', 'starting_equipment_item_id',
    'starting_equipment_item_id_equipped', 'starting_equipment_item_id_unequipped', 'spell_material_item_id', 'monster_bit'] as const;
const text = (value: unknown): string => value == null ? '' : typeof value === 'boolean' ? (value ? '1' : '')
    : typeof value === 'object' ? 'array' : String(value);

/** Validate outgoing authored rows, after provisioning. Catalog entries are read/serialized by the editor first. */
export function validateStructuredItemRows(data: Record<string, unknown>): AuthoringFinding[] {
    const errors: AuthoringFinding[] = [];
    for (const field of fields) {
        const payload = data[field];
        if (!Array.isArray(payload)) continue; // Legacy scalar and pipe payloads remain supported.
        const add = (message: string) => errors.push({ field, message });
        const hasObject = payload.some(entry => entry !== null && typeof entry === 'object');
        if (hasObject && !payload.every(entry => fromBaseRecord(entry) && Object.prototype.hasOwnProperty.call(entry, 'item_id'))) {
            add(`${field}: mixed or malformed row payload — every row must be an object with an item_id (no scalar/object mixing).`);
            continue;
        }
        payload.forEach((entry, index) => {
            const prefix = `${field} row ${index + 1}: `;
            if (!hasObject) {
                if (field !== 'monster_bit' || entry == null || text(entry).trim() === '') return;
                const [id = '', ...tail] = text(entry).trim().split('|');
                if (!isUuid(id)) add(`${prefix}'${id}' is not a valid item UUID.`);
                const qty = tail.join('|');
                if (tail.length && qty !== '' && !is_numeric(qty)) add(`${prefix}qty '${qty}' is not numeric.`);
                return;
            }
            if (!fromBaseRecord(entry)) return;
            const row = entry;
            // These are final flat rows. A stray catalog shell must not override the submitted identity.
            const id = text(itemsCol({ items: row }, 'item_id')).trim();
            if (typeof row.item_id !== 'string' || !isUuid(id)) add(`${prefix}'${id}' is not a valid item UUID.`);
            if (row.qty != null && row.qty !== '' && !is_numeric(row.qty)) add(`${prefix}qty '${text(row.qty)}' is not numeric.`);
            const template = text(row.template_id).trim();
            if (template !== '' && !isUuid(template)) add(`${prefix}template_id '${template}' is not a valid UUID.`);
        });
    }
    return errors;
}
